<template>
  <div
    v-if="ready"
    class="act-countdown"
    :class="locale"
  >
    <div
      class="countdown-item"
      v-for="item in items"
      :key="item.key"
    >
      <div class="title">{{ item.title }}</div>
      <div class="time" v-if="item.status === 'active'">
        <span class="box" v-if="item.parts.hasDay">
          {{ item.parts.day }}{{ $t('monopoly.day') }}
        </span>
        <span class="box">{{ item.parts.hour }}</span>
        <span class="sep">:</span>
        <span class="box">{{ item.parts.minute }}</span>
        <span class="sep">:</span>
        <span class="box">{{ item.parts.second }}</span>
      </div>
      <div class="status" v-else>
        {{ item.statusText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { formatCountdownParts, parseActTimeToMs } from '@/views/act/monopoly/utils/time'

const { t, locale } = useI18n()
const commonConfig = inject('monopolyCommonConfig', null)

const realTime = ref(0)
let baseServerMs = 0
let baseLocalMs = 0
let timer = null

const syncServerClock = () => {
  const serverMs = parseActTimeToMs(commonConfig?.serverTime)
  console.log('serverMs', serverMs)
  if (!serverMs || Number.isNaN(serverMs)) return
  baseServerMs = serverMs
  baseLocalMs = Date.now()
  realTime.value = serverMs
}

const tick = () => {
  if (!baseServerMs) return
  realTime.value = baseServerMs + (Date.now() - baseLocalMs)
}

const ready = computed(() => realTime.value > 0)

const buildItem = ({ key, title, endMs }) => {
  const now = realTime.value
  const remainMs = Number.isNaN(endMs) ? 0 : endMs - now
  if (remainMs > 0) {
    return {
      key,
      title,
      status: 'active',
      statusText: '',
      parts: formatCountdownParts(remainMs),
    }
  }
  return {
    key,
    title,
    status: 'ended',
    statusText: t('monopoly.countDownEnded'),
    parts: formatCountdownParts(0),
  }
}

const items = computed(() => {
  if (!realTime.value) return []
  return [
    buildItem({
      key: 'recharge',
      title: t('monopoly.rechargeCountDown'),
      endMs: parseActTimeToMs(commonConfig?.rechargeStatEndTime),
    }),
    buildItem({
      key: 'dice',
      title: t('monopoly.diceCountDown'),
      endMs: parseActTimeToMs(commonConfig?.activityPlayEndTime),
    }),
  ]
})

watch(
  () => commonConfig?.serverTime,
  () => {
    syncServerClock()
  },
  { immediate: true }
)

onMounted(() => {
  syncServerClock()
  timer = setInterval(tick, 1000)
})

onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})
</script>

<style lang="less" scoped>
.act-countdown {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 102;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
  pointer-events: none;

  .countdown-item {
    padding: 8px 12px;
    background: linear-gradient(to bottom, rgba(100, 221, 253, 0.95), rgba(46, 175, 236, 0.95));
    border: 2px solid #fff;
    border-radius: 8px;
    box-sizing: border-box;
    color: #fff;
    text-align: center;
    box-shadow: 0 4px 10px rgba(18, 33, 92, 0.15);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .title {
    font-size: 18px;
    font-weight: bold;
    line-height: 1.3;
    text-shadow: 0 1px 2px rgba(18, 33, 92, 0.35);
    white-space: nowrap;
  }

  .time {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-wrap: nowrap;
  }

  .box {
    min-width: 34px;
    height: 30px;
    padding: 0 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 6px;
    color: #12215c;
    font-size: 18px;
    font-weight: bold;
    box-sizing: border-box;
  }

  .sep {
    font-size: 18px;
    font-weight: bold;
    line-height: 1;
  }

  .status {
    font-size: 18px;
    font-weight: bold;
    line-height: 30px;
  }

  &.vi,
  &.th,
  &.id {
    .title {
      font-size: 14px;
    }
  }
}
</style>
